import React from 'react';
import { DatePicker, Tooltip } from 'antd';
import dayjs from 'dayjs';
import moment from 'moment-timezone';
import { DatePeriod } from 'shared/types';
import { getUserTimezoneOffset, offsetToTimeZone } from 'utils/date-utils';

type Props = {
  values: DatePeriod;
  setDates: (dates: DatePeriod) => void;
  isBlock?: boolean;
  disabled?: boolean;
  tooltip?: string;
  size?: 'small' | 'middle' | 'large';
};

// Pickers operate on calendar days in the admin's configured timezone, not the
// browser's. Stored values are ISO UTC instants representing midnight in that
// timezone; `startedAtEnd` is the day AFTER the last selected day so the range
// is half-open [start, end).
const DateRange: React.FC<Props> = ({ values, setDates, isBlock, disabled, tooltip, size = 'small' }): JSX.Element => {
  const { RangePicker } = DatePicker;
  const { startedAtStart, startedAtEnd } = values;
  const userZone = offsetToTimeZone(getUserTimezoneOffset());

  const isoToUserDay = (iso: string): dayjs.Dayjs | null => {
    if (!iso) return null;
    return dayjs(moment.utc(iso).tz(userZone).format('YYYY-MM-DD'));
  };

  const userMidnightISO = (d: dayjs.Dayjs): string =>
    moment.tz(`${d.format('YYYY-MM-DD')}T00:00:00`, userZone).toISOString();

  const START = isoToUserDay(startedAtStart);
  const END = startedAtEnd
    ? dayjs(moment.utc(startedAtEnd).tz(userZone).subtract(1, 'day').format('YYYY-MM-DD'))
    : null;

  const handleDateChange = (dates: (dayjs.Dayjs | null )[] | null): void => {
    if (!dates || dates.length === 0 || (dates[0] === null && dates[1] === null)) {
      setDates({ startedAtStart: '', startedAtEnd: '' });
      return;
    }

    const startedAtStartNew = dates[0] ? userMidnightISO(dates[0]) : '';
    const startedAtEndNew = dates[1]
      ? moment(userMidnightISO(dates[1])).add(1, 'day').toISOString()
      : '';

    setDates({ startedAtStart: startedAtStartNew, startedAtEnd: startedAtEndNew });
  };

  return (
    <Tooltip title={tooltip}>
      <RangePicker
        onChange={handleDateChange}
        value={[START, END]}
        format="MM/DD/YYYY"
        presets={[
          { label: 'Today', value: [dayjs().startOf('day'), dayjs().startOf('day')] },
          { label: 'This Month', value: [dayjs().startOf('month'), dayjs().endOf('month')] }
        ]}
        allowEmpty={[true, true]}
        allowClear={true}
        inputReadOnly={true}
        size={size}
        style={{ width: isBlock ? '180px' : '270px', display: isBlock ? 'block' : '' }}
        disabled={disabled}
      />
    </Tooltip>
  );
};

export default DateRange;
